import { baseApi } from "./baseApi";

export interface CartItem {
  id: string;
  productId: string;
  product: {
    id: string;
    name: string;
    price: number;
    image?: string;
  };
  quantity: number;
  subtotal: number;
  variations?: Record<string, string>; // Selected variation options: {"Size": "Large", "Spice Level": "Hot"}
  createdAt: string;
  updatedAt: string;
}

export interface Cart {
  id: string;
  userId?: string;
  sessionId?: string;
  items: CartItem[];
  totalAmount: number;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AddToCartData {
  productId: string;
  quantity: number;
  variations?: Record<string, string>; // Selected variation options: {"Size": "Large", "Spice Level": "Hot"}
}

export interface UpdateCartItemData {
  quantity: number;
}

/**
 * Cart API slice
 * Handles all cart-related API calls
 */
export const cartApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get cart
    getCart: builder.query<
      { success: boolean; data: Cart; message?: string },
      void
    >({
      query: () => ({
        url: "/cart",
        headers: {
          "x-session-id": localStorage.getItem("sessionId") || "",
        },
      }),
      providesTags: ["Cart"],
    }),

    // Add item to cart
    addToCart: builder.mutation<
      { success: boolean; data: CartItem; message?: string },
      AddToCartData
    >({
      query: (data) => ({
        url: "/cart",
        method: "POST",
        body: data,
        headers: {
          "x-session-id": localStorage.getItem("sessionId") || "",
        },
      }),
      invalidatesTags: ["Cart"],
    }),

    // Update cart item
    updateCartItem: builder.mutation<
      { success: boolean; data: CartItem; message?: string },
      { id: string; quantity: number }
    >({
      query: ({ id, quantity }) => ({
        url: `/cart/${id}`,
        method: "PUT",
        body: { quantity },
      }),
      invalidatesTags: ["Cart"],
    }),

    // Remove item from cart
    removeFromCart: builder.mutation<
      { success: boolean; message?: string },
      string
    >({
      query: (id) => ({
        url: `/cart/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Cart"],
    }),

    // Clear cart
    clearCart: builder.mutation<
      { success: boolean; message?: string },
      void
    >({
      query: () => ({
        url: "/cart",
        method: "DELETE",
        headers: {
          "x-session-id": localStorage.getItem("sessionId") || "",
        },
      }),
      invalidatesTags: ["Cart"],
    }),
  }),
});

export const {
  useGetCartQuery,
  useAddToCartMutation,
  useUpdateCartItemMutation,
  useRemoveFromCartMutation,
  useClearCartMutation,
} = cartApi;

